import { useEffect, useRef, useState } from 'react';

/**
 * Carga las imágenes por demanda: mantiene en memoria la de la posición actual y
 * `ventanaPrecarga` fechas a cada lado, para que el avance manual y la reproducción no
 * queden esperando la red.
 *
 * A diferencia de useLoadImages, da por hecho que todas las fechas existen —vienen del
 * índice que publica el servidor—, así que no usa el pedido para descubrir qué hay.
 *
 * Lo ya descargado no se descarta: al completarse una vuelta del bucle queda todo en
 * caché y la reproducción pasa a ser fluida. Los object-URL se liberan al desmontar.
 *
 * @param {Object} props
 * @param {string[]} props.fechas - fechas disponibles, de la más nueva a la más vieja.
 * @param {number} props.posicion - índice actual dentro de `fechas`.
 * @param {number} props.ventanaPrecarga - cuántas fechas precargar a cada lado.
 * @returns {{images: Object, error: Error|null}} images indexa object-URLs por fecha.
 */
const usePrecargaImagenes = ({ prefijo, sufijo, extencion, IMAGE_BASE_PATH, fechas, posicion, ventanaPrecarga }) => {
    const [images, setImages] = useState({});
    const [error, setError] = useState(null);

    // Fechas ya pedidas: en vuelo o descargadas. Las que fallan se quitan para poder
    // reintentarlas la próxima vez que caigan en la ventana.
    const pedidas = useRef(new Set());
    const montado = useRef(true);
    useEffect(() => () => { montado.current = false }, []);

    useEffect(() => {
        if (fechas.length === 0) return;

        const faltantes = fechasVecinas({ fechas, posicion, ventanaPrecarga })
            .filter((fecha) => !pedidas.current.has(fecha));
        if (faltantes.length === 0) return;

        faltantes.forEach((fecha) => pedidas.current.add(fecha));

        Promise.allSettled(faltantes.map(async (fecha) => {
            const src = `${IMAGE_BASE_PATH}${prefijo}${fecha}${sufijo}.${extencion}`;
            const respuesta = await fetch(src);
            if (!respuesta.ok) throw Object.assign(new Error(`No se pudo cargar ${src} (HTTP ${respuesta.status})`), { fecha });
            return { fecha, objectURL: URL.createObjectURL(await respuesta.blob()) };
        })).then((resultados) => {
            const nuevas = {};
            resultados.forEach((resultado, indice) => {
                if (resultado.status === 'fulfilled') {
                    nuevas[resultado.value.fecha] = resultado.value.objectURL;
                    return;
                }
                pedidas.current.delete(resultado.reason?.fecha ?? faltantes[indice]);
                setError(resultado.reason);
            });

            // No se descartan los resultados de un ciclo anterior: como el effect vuelve a
            // correr en cada cambio de posición, hacerlo perdería las imágenes en vuelo.
            if (!montado.current) {
                Object.values(nuevas).forEach((objectURL) => URL.revokeObjectURL(objectURL));
                return;
            }
            if (Object.keys(nuevas).length > 0) setImages((previas) => ({ ...previas, ...nuevas }));
        });
    }, [fechas, posicion, ventanaPrecarga, prefijo, sufijo, extencion, IMAGE_BASE_PATH]);

    // Libera los blobs al desmontar.
    const imagenesVigentes = useRef(images);
    imagenesVigentes.current = images;
    useEffect(() => () => {
        Object.values(imagenesVigentes.current).forEach((objectURL) => URL.revokeObjectURL(objectURL));
    }, []);

    return { images, error };
};

/**
 * Fechas a tener en memoria alrededor de `posicion`, empezando por la actual y
 * alternando hacia atrás y hacia adelante.
 *
 * La vecindad es circular porque el bucle también lo es: `imagenSiguiente` salta de la
 * posición 0 a la última (ver Players/controls.js), así que los extremos son vecinos.
 */
const fechasVecinas = ({ fechas, posicion, ventanaPrecarga }) => {
    const desplazamientos = [0];
    for (let distancia = 1; distancia <= ventanaPrecarga; distancia++) desplazamientos.push(-distancia, distancia);

    return desplazamientos.reduce((vecinas, desplazamiento) => {
        const indice = (((posicion + desplazamiento) % fechas.length) + fechas.length) % fechas.length;
        if (!vecinas.includes(fechas[indice])) vecinas.push(fechas[indice]);
        return vecinas;
    }, []);
};

export { fechasVecinas };
export default usePrecargaImagenes;
